const getUserAgent = (win: Window): string =>
	win && win.navigator && win.navigator.userAgent ? win.navigator.userAgent.toLowerCase() : ''

const isSafari = (win: Window): boolean => {
	const userAgent = getUserAgent(win)
	return userAgent.indexOf('safari') > -1 && userAgent.indexOf('version') > -1
}

const isIOS = (win: Window): boolean => {
	const platform = (win && win.navigator && win.navigator.platform) || ''
	return !!platform && /iPad|iPhone|iPod/.test(platform)
}

/**
 * Detects if the browser supports CSS container style queries.
 * Works in both browser and SSR contexts.
 *
 * In SSR context: Returns true (we assume container style queries are supported).
 * Uses a cached result to avoid repeated DOM operations.
 */
export const supportsContainerStyleQueries = (): boolean => {
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		return true
	}

	if (typeof (globalThis as any).supportsContainerStyleQueriesFlag !== 'undefined') {
		return (globalThis as any).supportsContainerStyleQueriesFlag
	}

	try {
		// Safari/iOS don't apply container style queries after relayout.
		// Treat them as unsupported so the polyfill stamps `data-preset`
		// attributes on every resize.
		if (isSafari(window) || isIOS(window)) {
			;(globalThis as any).supportsContainerStyleQueriesFlag = false
			return false
		}

		// Feature-detection probe: insert a hidden container + child, declare
		// a @container style() rule that sets a detector custom property on
		// the child, and check whether the engine resolved the rule.
		const container = document.createElement('div')
		const child = document.createElement('div')
		const style = document.createElement('style')

		container.style.cssText = '--test:1;position:absolute;top:-9999px;width:0px;height:0px;display:none;'
		container.appendChild(child)

		style.textContent = '@container style(--test:1){div{--detected:yes}}'

		document.head.appendChild(style)
		document.body.appendChild(container)

		const isSupported = getComputedStyle(child).getPropertyValue('--detected').trim() === 'yes'

		document.body.removeChild(container)
		document.head.removeChild(style)
		;(globalThis as any).supportsContainerStyleQueriesFlag = isSupported
		return isSupported
	} catch {
		;(globalThis as any).supportsContainerStyleQueriesFlag = false
		return false
	}
}
